import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Typography } from '@mui/material';

import CorporationIcon from '../../../assets/icons/CorporationIcon.svg';
import PercentIcon from '../../../assets/icons/PercentIcon.svg';
import StatusIcon from '../../../assets/icons/StatusIconInFilters.svg';
import DateRangePickerFilter from '../../../components/DateRangePickerFilter/DateRangePickerFilter';
import FilterSwitch from '../../../components/FilterSwitch';
import FreeTextSearchFilter, {
  SEARCHBAR_BACKGROUND,
} from '../../../components/FreeTextSearchFilter';
import MultipleSelectDropdownChip from '../../../components/MultipleSelectDropdownChip/MultipleSelectDropdownChip';
import { SearchableSelectDropdownFilterDeprecated } from '../../../components/SearchableSelectDropdownFilter/SearchableSelectDropdownFilter';
import { SelectDropdownFilter } from '../../../components/SelectDropdownFilter/SelectDropdownFilter';
import UserSearchOption, {
  USER_SEARCH_DROPDOWN_MIN_WIDTH,
} from '../../../components/UserSearchOption/UserSearchOption';
import {
  FIVE_AM_IN_MIN,
  NINE_THIRTY_PM_IN_MIN,
  ONE_THIRTY_PM_IN_MIN,
  SEARCH_PARAM_KEYS,
  SHIFT_OPTION_KEYS,
} from '../../../constants/jobOrderConstants';
import { getTimeZone } from '../../../utils';
import { getInitialSelectedShiftOptions, getNewShiftFilters } from '../../../utils/jobOrderHelpers';
import parseQueryParamToSafeInteger from '../../../utils/queryParamParsers';

const corporationsAPICallback = {
  httpMethod: 'POST',
  route: 'corporations/read',
  generateBody: (searchTerm) => ({
    filters: [{ operation: 'icontains', field: 'name', value: searchTerm }],
  }),
};

const associatedUserAPICallback = (userId) => ({
  httpMethod: 'POST',
  route: 'V2/users/read',
  generateBody: (searchTerm) => {
    const filters = userId
      ? [{ operation: 'equalsID', field: '_id', value: userId }]
      : [
          {
            operation: 'includedIn',
            field: 'role',
            value: [UserRole.ADMIN, UserRole.RECRUITER],
          },
          ...(searchTerm ? [{ operation: 'icontains', field: 'name', value: searchTerm }] : []),
        ];
    return { filters };
  },
});

const fillRateOptions = [
  { value: 'true', name: 'Full Shifts' },
  { value: 'false', name: 'Unfilled Shifts' },
];

const shiftOptionsPayload = {
  [SHIFT_OPTION_KEYS.FIVE_AM_TO_ONE_THIRTY_PM]: {
    field: 'shift',
    operation: 'betweenClockTimes',
    value: { start: FIVE_AM_IN_MIN, end: ONE_THIRTY_PM_IN_MIN, zone: getTimeZone() },
  },
  [SHIFT_OPTION_KEYS.ONE_THIRTY_PM_TO_NINE_THIRTY_PM]: {
    field: 'shift',
    operation: 'betweenClockTimes',
    value: { start: ONE_THIRTY_PM_IN_MIN, end: NINE_THIRTY_PM_IN_MIN, zone: getTimeZone() },
  },
  [SHIFT_OPTION_KEYS.NINE_THIRTY_PM_TO_FIVE_AM]: {
    field: 'shift',
    operation: 'betweenClockTimes',
    value: { start: NINE_THIRTY_PM_IN_MIN, end: FIVE_AM_IN_MIN, zone: getTimeZone() },
  },
};

const shiftOptions = Object.keys(shiftOptionsPayload);

const JobOrdersFilters = ({ showAssociatedUserFilter, showCorporationFilter }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const setQueryParams = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams);

      Object.entries(patch).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });

      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const initialFillRateValue = useMemo(() => {
    const value = searchParams.get(SEARCH_PARAM_KEYS.FILL_RATE);
    return fillRateOptions.find((fillRate) => value === fillRate.value) || null;
  }, [searchParams]);

  const initialDateRange = useMemo(() => {
    const startDateParam = searchParams.get(SEARCH_PARAM_KEYS.START_DATE);
    const endDateParam = searchParams.get(SEARCH_PARAM_KEYS.END_DATE);
    const startDate = parseQueryParamToSafeInteger(startDateParam);
    const endDate = parseQueryParamToSafeInteger(endDateParam);
    return startDate !== null && endDate !== null ? [startDate, endDate] : null;
  }, [searchParams]);

  const initialSelectedShifts = useMemo(
    () =>
      getInitialSelectedShiftOptions(
        searchParams.get(SEARCH_PARAM_KEYS.SHIFT_START),
        searchParams.get(SEARCH_PARAM_KEYS.SHIFT_END)
      ),
    [searchParams]
  );

  const handleCorporationChange = useCallback(
    (corporationId) => {
      setQueryParams({ [SEARCH_PARAM_KEYS.CORPORATION]: corporationId || null });
    },
    [setQueryParams]
  );

  const handleAssociatedUserChange = useCallback(
    (userId) => {
      const cleared = !userId || (Array.isArray(userId) && userId.length === 0);
      setQueryParams({ [SEARCH_PARAM_KEYS.ASSOCIATED_USER]: cleared ? null : userId });
    },
    [setQueryParams]
  );

  const selectedAssociatedUserId = searchParams.get(SEARCH_PARAM_KEYS.ASSOCIATED_USER);

  const handleFillRateChange = useCallback(
    (fillRate) => {
      setQueryParams({ [SEARCH_PARAM_KEYS.FILL_RATE]: fillRate || null });
    },
    [setQueryParams]
  );

  const handleDateRangeChange = useCallback(
    (newDateRange) => {
      const startDate = newDateRange.find((date) => date.field === 'start')?.value;
      const endDate = newDateRange.find((date) => date.field === 'end')?.value;

      if (startDate && endDate) {
        setQueryParams({
          [SEARCH_PARAM_KEYS.START_DATE]: startDate,
          [SEARCH_PARAM_KEYS.END_DATE]: endDate,
        });
        return;
      }

      setQueryParams({
        [SEARCH_PARAM_KEYS.START_DATE]: null,
        [SEARCH_PARAM_KEYS.END_DATE]: null,
      });
    },
    [setQueryParams]
  );

  const handleStatusChange = useCallback(
    (newStatus) => {
      // Default is Active ("false") when param is missing; we don't force defaults into URL.
      setQueryParams({ [SEARCH_PARAM_KEYS.STATUS]: newStatus || null });
    },
    [setQueryParams]
  );

  const handleShiftOptionChange = useCallback(
    (selectedShifts) => {
      if (!Array.isArray(selectedShifts)) {
        setQueryParams({
          [SEARCH_PARAM_KEYS.SHIFT_START]: null,
          [SEARCH_PARAM_KEYS.SHIFT_END]: null,
        });
        return;
      }
      const selectedShiftFilters = selectedShifts
        .map((shift) => shiftOptionsPayload[shift])
        .filter(Boolean);
      const parsedShiftTime = getNewShiftFilters(selectedShiftFilters);
      const shiftStart = parsedShiftTime?.[0]?.value?.start;
      const shiftEnd = parsedShiftTime?.[0]?.value?.end;

      if (shiftStart !== undefined && shiftEnd !== undefined) {
        setQueryParams({
          [SEARCH_PARAM_KEYS.SHIFT_START]: shiftStart,
          [SEARCH_PARAM_KEYS.SHIFT_END]: shiftEnd,
        });
        return;
      }

      setQueryParams({
        [SEARCH_PARAM_KEYS.SHIFT_START]: null,
        [SEARCH_PARAM_KEYS.SHIFT_END]: null,
      });
    },
    [setQueryParams]
  );

  const handlePositionSearchChange = useCallback(
    (newPositionFilter) => {
      const positionSearchTerm = newPositionFilter?.[0]?.value;
      setQueryParams({ [SEARCH_PARAM_KEYS.POSITION]: positionSearchTerm || null });
    },
    [setQueryParams]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '12px',
      }}
    >
      <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
        {LanguageConverterV2('buttonText.filters')}
      </Typography>

      {showCorporationFilter && (
        <SearchableSelectDropdownFilterDeprecated
          placeholder="Corporation"
          onValueChange={handleCorporationChange}
          optionsAPICallback={corporationsAPICallback}
          getOptionLabel={(option) => option.name}
          background={SEARCHBAR_BACKGROUND.DEFAULT}
          initialValue={searchParams.get(SEARCH_PARAM_KEYS.CORPORATION)}
        />
      )}

      {showAssociatedUserFilter && (
        <SearchableSelectDropdownFilterDeprecated
          placeholder={LanguageConverterV2('jobOrders.filter.associatedUser')}
          onValueChange={handleAssociatedUserChange}
          optionsAPICallback={associatedUserAPICallback(selectedAssociatedUserId)}
          getOptionLabel={(option) => option.name}
          renderOptionContent={(option) => <UserSearchOption user={option} />}
          dropdownMinWidth={USER_SEARCH_DROPDOWN_MIN_WIDTH}
          background={SEARCHBAR_BACKGROUND.DEFAULT}
          initialValue={selectedAssociatedUserId}
        />
      )}

      <SelectDropdownFilter
        placeholder="Fill Rate"
        onValueChange={handleFillRateChange}
        options={fillRateOptions}
        getOptionLabel={(option) => option.name}
        startAdornmentIcon={
          <Box
            component="img"
            sx={{
              height: 13,
              width: 12,
            }}
            alt="Fill Rate"
            src={PercentIcon}
          />
        }
        wrapperSx={{ width: '180px', padding: '0px 6px' }}
        initialValue={initialFillRateValue}
      />

      <DateRangePickerFilter
        placeholder="Dates"
        onValueChange={handleDateRangeChange}
        field={{ start: 'start', end: 'end' }}
        operation={{ start: 'onOrAfter', end: 'onOrBefore' }}
        initialValue={initialDateRange}
      />

      <FilterSwitch
        leftLabel={LanguageConverterV2('jobOrder.status.active')}
        rightLabel={LanguageConverterV2('jobOrder.status.cancelled')}
        leftValue="false"
        rightValue="true"
        currentValue={searchParams.get(SEARCH_PARAM_KEYS.STATUS) || 'false'}
        onValueChange={handleStatusChange}
        startAdornmentIcon={
          <Box
            component="img"
            sx={{
              height: 16,
              width: 16,
            }}
            alt="Status"
            src={StatusIcon}
          />
        }
        testId="status-filter-switch"
      />

      <MultipleSelectDropdownChip
        options={shiftOptions}
        placeholder="Shifts"
        startAdornmentIcon={
          <Box
            component="img"
            sx={{
              height: 16,
              width: 15,
            }}
            alt="Shifts"
            src={CorporationIcon}
          />
        }
        allOptionsLabel="All Shifts"
        onValueChange={handleShiftOptionChange}
        defaultSelectedOptions={initialSelectedShifts}
      />

      <FreeTextSearchFilter
        placeholder="Position Name"
        onValueChange={handlePositionSearchChange}
        field="name"
        operation="icontains"
        background={SEARCHBAR_BACKGROUND.DEFAULT}
        initialValue={{ value: searchParams.get(SEARCH_PARAM_KEYS.POSITION) }}
      />
    </Box>
  );
};

JobOrdersFilters.propTypes = {
  showAssociatedUserFilter: PropTypes.bool,
  showCorporationFilter: PropTypes.bool,
};

JobOrdersFilters.defaultProps = {
  showAssociatedUserFilter: false,
  showCorporationFilter: false,
};

export default JobOrdersFilters;
